// Карточка судна — тот же живой каркас, что у сделки, компании и контакта
// (components/card): слева вкладки «Общие»/«Сделки по судну», справа лента. Полосы
// стадий нет — у судна нет воронки.
//
// Откуда данные. У ядра есть витрина судов (GET /vessels), но она ищет по названию и IMO
// и НЕ умеет отдать судно по id. Поэтому карточка читает контакт (id судна = id контакта),
// а IMO/MMSI/флаг/порт достаёт из полей контакта — теми же кодами, которыми витрина
// сама себя наливает (VESSEL_FIELDS в lib/entities). Эти поля зеркальные — капсула
// Bitrix и никакой правки.
//
// Живая часть — своя схема ядра: лента (вехами служат сделки судна из своей схемы),
// комментарии и «Наши поля» (entity=судно). Свой id приезжает в `_OWN.id` ответа
// /own/vessel — это номер таблицы vessel, отдельной от людей.
//
// РАЗДЕЛЫ ПОЛЕЙ — ПО ЖИВОМУ BITRIX, макет ТОТ ЖЕ, что у контакта (в Bitrix судно и есть
// контакт, и раскладка у них одна — снята из DOM 31.07.2026, api/entityLayouts.ts).
// На судне видны ВСЕ разделы, включая пустые верификации: пустой раздел здесь — ответ
// («верификаций не было»), а не шум. /own/vessel отдаёт лишь 4 судовых UF-поля, поэтому
// остальные (47 полей короткой верификации, документы БТО) карточка доспрашивает у
// зеркала — useСырьёКарточкиBitrix тем же контактным методом.
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import {
  добавитьКомментарий, ownIdКарточки, почемуНедоступно, событияЛенты,
  сохранитьЗначение, словами, useЛентаКарточки, useНашиПоля, КЛЮЧ_НАШИХ_ПОЛЕЙ,
} from '../api/cardLiveEntities'
import {
  МАКЕТ_КОНТАКТА, заметкаСырья, слитьДляРаскладки, useСырьёКарточкиBitrix,
} from '../api/entityLayouts'
import { useContact, useDeals, useFields, useRefs, useRightsApi } from '../api/hooks'
import { ПОДСКАЗКА_НЕТ_СОЗДАНИЯ, подсказкаПустойДаты } from '../api/istochnik'
import { CardShell } from '../components/card/CardShell'
import type { CardTab } from '../components/card/CardShell'
import type { CardField } from '../components/card/FieldRow'
import { FieldSection } from '../components/card/FieldSection'
import { Timeline } from '../components/card/Timeline'
import type { CommentResult, TimelineEvent } from '../components/card/Timeline'
import { EntityLink } from '../components/EntityLink'
import { OwnFieldsSection } from '../components/OwnFieldsSection'
import { QueryState } from '../components/QueryState'
import {
  DEAL_VESSEL_FIELD, VESSEL_FIELDS, entityPath, isVesselContact, vesselName,
} from '../lib/entities'
import { fmtDate, fmtMoney, fmtText } from '../lib/format'

import { РазделыBitrix } from '../components/cardEntities'

const RELATED = 20

type Вкладка = 'main' | 'deals'

export default function VesselCard() {
  const { id } = useParams<{ id: string }>()
  const q = useContact(id)
  const refs = useRefs()
  // Справочник контакта, а не отдельный «судовой»: судно и есть контакт, поля лежат там же.
  const f = useFields('contact')
  const rights = useRightsApi()
  const qc = useQueryClient()
  const [вкладка, setВкладка] = useState<Вкладка>('main')

  // Сделки по судну: в сделке судно хранится полем UF_CRM_1502613831 (id контакта-судна).
  const deals = useDeals({
    filter: id ? { [DEAL_VESSEL_FIELD]: id } : {},
    order: { DATE_MODIFY: 'DESC' },
    select: ['ID', 'TITLE', 'STAGE_ID', 'COMPANY_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'DATE_MODIFY'],
  })

  const c = q.data
  const судно = c ? isVesselContact(c) : false
  const name = c ? vesselName(c) : ''
  // Обычный человек, открытый как судно, — не судно: его свой id лежит в таблице людей,
  // и лента «судна» по нему была бы чужой лентой.
  const ownId = судно ? ownIdКарточки(c) : null

  // Сырьё зеркала: судно в зеркале — контакт, и все его UF-поля (верификации,
  // документы БТО) лежат в raw contacts. /own/vessel отдаёт лишь 4 судовых кода.
  const сырьё = useСырьёКарточкиBitrix('crm.contact.get', id)

  const лента = useЛентаКарточки('судно', ownId)
  const наши = useНашиПоля('судно', ownId)

  const [сессионные, setСессионные] = useState<TimelineEvent[]>([])
  const добавитьСобытие = (с: Omit<TimelineEvent, 'id' | 'at'>) =>
    setСессионные((были) => [
      ...были,
      { ...с, id: `s-${были.length}`, at: new Date().toISOString() },
    ])

  const события = useMemo<TimelineEvent[]>(() => {
    const из: TimelineEvent[] = [...сессионные, ...событияЛенты(лента.data?.pages)]
    if (c?.DATE_CREATE) {
      из.push({
        id: 'created', at: String(c.DATE_CREATE), kind: 'bitrix',
        text: 'Судно заведено', origin: 'bitrix',
      })
    }
    return из
  }, [сессионные, лента.data, c])

  // Обычному человеку, открытому по адресу судна, лента судна не принадлежит —
  // причина показана словами, а не выдумана «не перенесена».
  const заметкаЛенты = c && !судно
    ? 'Эта запись — не судно: лента живёт на карточке контакта.'
    : почемуНедоступно(ownId, лента.error)

  const отправитьКомментарий = async (текст: string): Promise<CommentResult> => {
    if (ownId === null) return { ok: false, почему: заметкаЛенты }
    try {
      const с = await добавитьКомментарий('судно', ownId, текст)
      добавитьСобытие({ kind: 'comment', text: String(с.text ?? текст), origin: 'crm' })
      return { ok: true }
    } catch (e) {
      return { ok: false, почему: словами(e) }
    }
  }

  const сохранитьНашеПоле = async (код: string, значение: unknown) => {
    await сохранитьЗначение('судно', ownId!, код, значение)
    const подпись = наши.data?.custom.find((п) => п.code === код)?.label ?? код
    добавитьСобытие({ kind: 'field', text: `Изменено поле «${подпись}»`, origin: 'crm' })
    await qc.invalidateQueries({ queryKey: [КЛЮЧ_НАШИХ_ПОЛЕЙ, 'судно', ownId] })
  }

  const вкладки: CardTab[] = [
    { key: 'main', label: 'Общие' },
    {
      key: 'deals',
      label: deals.data && !deals.data.stale
        ? `Сделки по судну · ${deals.data.total}`
        : 'Сделки по судну',
    },
  ]

  return (
    <>
      <div className="page-head">
        <h1>{name || `Судно #${id}`}</h1>
        <div className="spacer" />
        <Link to="/vessels">← к списку</Link>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        {c && (
          <>
            {/* Ссылку на судно можно получить откуда угодно (поле сделки, чужой адрес),
                а по id ядро не проверяет, судно это или человек. Проверяем сами. */}
            {!судно && (
              <div className="notice">
                Контакт #{c.ID} не помечен как судно: в Bitrix у судов фамилия начинается
                со «Судно», а здесь — «{fmtText(c.LAST_NAME)}». Поля IMO и порта у обычного
                контакта пустые.{' '}
                <Link to={entityPath('contact', c.ID)}>Открыть как контакт →</Link>
              </div>
            )}

            <CardShell
              head={
                <div className="cshell-head">
                  <div className="cshell-title">{fmtText(name)}</div>
                  <div className="spacer" />
                  {c[VESSEL_FIELDS.imo]
                    ? <span className="muted mono">IMO {fmtText(c[VESSEL_FIELDS.imo])}</span>
                    : null}
                </div>
              }
              tabs={вкладки}
              active={вкладка}
              onTab={(k) => setВкладка(k as Вкладка)}
              left={
                <>
                  {вкладка === 'main' && (
                    <Поля c={c} f={f} refs={refs} rights={rights} ownId={ownId} наши={наши}
                          сырьё={сырьё} судно={судно}
                          почемуНаши={!судно
                            ? 'Эта запись — не судно: свои поля живут на карточке контакта.'
                            : undefined}
                          onSaveExt={rights.canWrite && ownId !== null ? сохранитьНашеПоле : undefined} />
                  )}
                  {вкладка === 'deals' && <Сделки deals={deals} refs={refs} />}
                </>
              }
              right={
                <>
                  <h2>Лента</h2>
                  <Timeline
                    events={события}
                    onComment={rights.canWrite && ownId !== null ? отправитьКомментарий : undefined}
                    note={заметкаЛенты}
                    more={{
                      has: !!лента.hasNextPage,
                      loading: лента.isFetchingNextPage,
                      onMore: () => { void лента.fetchNextPage() },
                    }}
                    emptyText="Событий в ленте нет"
                  />
                </>
              }
            />
          </>
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════ вкладка «Общие» ═══════════════════════

/**
 * Разделы — по живому Bitrix, макет контакта (в Bitrix судно — контакт, макет один).
 * На судне разделы видны все, включая пустые. Плюс «Служебные» (даты и то, что знает
 * только своя схема: год постройки, тип судна) и «Наши поля» из конструктора.
 */
function Поля({ c, f, refs, rights, ownId, наши, сырьё, судно, почемуНаши, onSaveExt }: {
  c: NonNullable<ReturnType<typeof useContact>['data']>
  f: ReturnType<typeof useFields>
  refs: ReturnType<typeof useRefs>
  rights: ReturnType<typeof useRightsApi>
  ownId: number | null
  наши: ReturnType<typeof useНашиПоля>
  сырьё: ReturnType<typeof useСырьёКарточкиBitrix>
  судно: boolean
  почемуНаши?: string
  onSaveExt?: (code: string, value: unknown) => Promise<void>
}) {
  const запись = слитьДляРаскладки(c, сырьё.data?.запись)
  const own = (c._OWN ?? {}) as Record<string, unknown>

  const особые: Record<string, CardField | null> = {
    COMPANY_ID: {
      code: 'COMPANY_ID', label: f.titleOr('COMPANY_ID', 'Компания'), type: 'text',
      origin: 'bitrix', readOnly: true, value: c.COMPANY_ID || null,
      display: <EntityLink kind="company" id={c.COMPANY_ID} resolve />,
      hint: 'компания, привязанная к контакту-судну в Bitrix; это не всегда судовладелец —'
        + ' в сделке заказчик может быть другой',
    },
    REQUISITES: {
      code: 'REQUISITES', label: 'Реквизиты', type: 'text', origin: 'bitrix', readOnly: true,
      value: null, display: <span className="frow-empty">—</span>,
      hint: 'реквизитов у контакта-судна не бывает; у компаний они в отдельном справочнике Bitrix',
    },
  }

  const служебные: CardField[] = [
    { code: 'DATE_CREATE', label: 'Создано', type: 'date', value: c.DATE_CREATE,
      origin: 'bitrix', readOnly: true, display: fmtDate(c.DATE_CREATE, true),
      hint: подсказкаПустойДаты(c.DATE_CREATE, ПОДСКАЗКА_НЕТ_СОЗДАНИЯ) },
    { code: 'DATE_MODIFY', label: 'Изменено', type: 'date', value: c.DATE_MODIFY,
      origin: 'bitrix', readOnly: true, display: fmtDate(c.DATE_MODIFY, true),
      hint: подсказкаПустойДаты(c.DATE_MODIFY) },
    // Что знает только своя схема (в Bitrix этих полей нет — капсулы нет тоже).
    ...(own.built_year ? [{ code: 'built_year', label: 'Год постройки', type: 'text',
      value: own.built_year, readOnly: true } as CardField] : []),
    ...(own.vessel_type ? [{ code: 'vessel_type', label: 'Тип судна', type: 'text',
      value: own.vessel_type, readOnly: true } as CardField] : []),
  ]

  return (
    <div className="card">
      <div className="hint" style={{ marginBottom: 10 }}>
        Судно в Bitrix — это контакт: карточка читает его напрямую
        (<Link to={entityPath('contact', c.ID)}>контакт #{c.ID}</Link>), потому что
        витрина судов ищет только по названию и IMO. Разделы — те же, что на карточке
        контакта в Bitrix: макет у них общий.
        {!f.loaded && ' Справочник полей недоступен — названия показаны по снимку раскладки 31.07.2026.'}
      </div>
      <РазделыBitrix макет={МАКЕТ_КОНТАКТА} запись={запись} f={f} refs={refs}
                     особые={особые} прятатьПустые={!судно} заметка={заметкаСырья(сырьё)} />
      <FieldSection title="Служебные" fields={служебные} />
      <OwnFieldsSection сущностьМн="судов" ownId={ownId} наши={наши}
                        rights={rights} onSave={onSaveExt} почему={почемуНаши} />
    </div>
  )
}

// ═══════════════════════ вкладка «Сделки по судну» ═══════════════════════

function Сделки({ deals, refs }: {
  deals: ReturnType<typeof useDeals>
  refs: ReturnType<typeof useRefs>
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Сделки по судну</h2>
        <div className="spacer" />
        <span className="muted">
          {deals.data && !deals.data.stale ? `всего: ${deals.data.total}` : ''}
        </span>
      </div>
      {/* Сужение правами: судно за человеком не закреплено, а сделки по нему —
          закреплены, и в чужой зоне их не видно. Молчать об этом нельзя. */}
      <QueryState isLoading={deals.isLoading} error={deals.error}
                  stale={deals.data?.stale} ageMin={deals.data?.ageMin}
                  scope={deals.data?.scope}>
        {(deals.data?.items ?? []).length === 0 ? (
          <div className="muted">
            Сделок с этим судном нет. Считаем по полю сделки «Судно»
            (<span className="mono">{DEAL_VESSEL_FIELD}</span>): если судно указали
            только текстом в названии, такая сделка сюда не попадёт.
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Название</th>
                  <th className="nowrap">Стадия</th>
                  <th className="num">Сумма</th>
                  <th className="nowrap">Изменена</th>
                </tr>
              </thead>
              <tbody>
                {(deals.data?.items ?? []).slice(0, RELATED).map((d) => (
                  <tr key={d.ID}>
                    <td className="mono nowrap" data-label="ID">
                      <EntityLink kind="deal" id={d.ID} title={d.ID} />
                    </td>
                    <td data-label="Название">
                      <EntityLink kind="deal" id={d.ID} title={fmtText(d.TITLE)} />
                    </td>
                    <td className="nowrap" data-label="Стадия">
                      <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>
                        {refs.stageName(d.STAGE_ID)}
                      </span>
                    </td>
                    <td className="num" data-label="Сумма">{fmtMoney(d.OPPORTUNITY, d.CURRENCY_ID || 'RUB')}</td>
                    <td className="nowrap muted" data-label="Изменена"
                        title={подсказкаПустойДаты(d.DATE_MODIFY)}>{fmtDate(d.DATE_MODIFY, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(deals.data?.total ?? 0) > RELATED && (
          <div className="hint" style={{ marginTop: 10 }}>
            Показаны первые {RELATED} из {deals.data?.total} по дате изменения.
          </div>
        )}
      </QueryState>
    </div>
  )
}
